import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PipelineAlgorithmSwitcherComponent } from './pipeline-algorithm-switcher.component';
import { AlgorithmSwitcherState } from '../../../state/algorithm-switcher.state';
import type { SwitchAlgorithmData } from '../../../state/algorithm-switcher.state';

export type SwitchAlgorithmDialogResult = string | 'cancel';

/**
 * Dialog host for `PipelineAlgorithmSwitcherComponent`.
 *
 * Bridges Material's dialog API onto the shared `AlgorithmSwitcherState`:
 * seeds the data into the state at construction time and closes the dialog
 * with the appropriate result when the state emits cancellation / acceptance.
 */
@Component({
  selector: 'ts-switch-algorithm-dialog',
  template: `<ts-pipeline-algorithm-switcher></ts-pipeline-algorithm-switcher>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, MatDialogModule, PipelineAlgorithmSwitcherComponent],
})
export class SwitchAlgorithmDialogComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Optional()
    public readonly dialogRef: MatDialogRef<SwitchAlgorithmDialogComponent, SwitchAlgorithmDialogResult> | null,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    data: SwitchAlgorithmData | null,
    private readonly switcherState: AlgorithmSwitcherState
  ) {
    if (data) {
      this.switcherState.setData(data);
    }
  }

  ngOnInit(): void {
    this.switcherState.cancellation$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.dialogRef?.close('cancel'));

    this.switcherState.acceptance$
      .pipe(takeUntil(this.destroy$))
      .subscribe((algorithmId) => this.dialogRef?.close(algorithmId));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
